import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    UploadCloudIcon,
    FileIcon,
    CheckCircle2Icon,
    XCircleIcon,
    Loader2Icon,
    Trash2Icon,
} from 'lucide-react';
import logo from '@/assets/Humeos_logotype_RGB_positive.png';
import {
    presignUploads,
    uploadFileToS3,
    validateUploadToken,
    type PublicUploadSession,
} from '@/services/uploadSession.service';

const ACCEPTED = ['.json', '.jpg', '.jpeg', '.jp2'];

type ItemStatus = 'pending' | 'uploading' | 'done' | 'error';
interface UploadItem {
    file: File;
    status: ItemStatus;
    progress: number;
    error?: string;
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024 * 1024)
        return `${(bytes / 1024 ** 3).toFixed(2)} Go`;
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 ** 2).toFixed(1)} Mo`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
    return `${bytes} o`;
}

function getErrorMessage(err: unknown, fallback: string): string {
    const data = (err as { response?: { data?: { message?: string } } })
        ?.response?.data;
    if (data?.message) return data.message;
    return fallback;
}

function isAccepted(name: string): boolean {
    const lower = name.toLowerCase();
    return ACCEPTED.some((ext) => lower.endsWith(ext));
}

export default function UploadPage() {
    const { token = '' } = useParams();
    const [phase, setPhase] = useState<
        'validating' | 'invalid' | 'expired' | 'ready'
    >('validating');
    const [session, setSession] = useState<PublicUploadSession | null>(null);
    const [items, setItems] = useState<UploadItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        let cancelled = false;
        validateUploadToken(token)
            .then((info) => {
                if (cancelled) return;
                setSession(info);
                setPhase('ready');
            })
            .catch((err) => {
                if (cancelled) return;
                const status = (err as { response?: { status?: number } })
                    ?.response?.status;
                setPhase(status === 410 ? 'expired' : 'invalid');
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const addFiles = useCallback((fileList: FileList | null) => {
        if (!fileList) return;
        const incoming = Array.from(fileList);
        setItems((prev) => {
            const existing = new Set(
                prev.map((i) => `${i.file.name}:${i.file.size}`),
            );
            const next = [...prev];
            for (const file of incoming) {
                const key = `${file.name}:${file.size}`;
                if (existing.has(key)) continue;
                existing.add(key);
                next.push({
                    file,
                    status: isAccepted(file.name) ? 'pending' : 'error',
                    progress: 0,
                    error: isAccepted(file.name)
                        ? undefined
                        : 'Type de fichier non accepté',
                });
            }
            return next;
        });
    }, []);

    const removeItem = (index: number) =>
        setItems((prev) => prev.filter((_, i) => i !== index));

    const updateItem = (index: number, patch: Partial<UploadItem>) =>
        setItems((prev) =>
            prev.map((it, i) => (i === index ? { ...it, ...patch } : it)),
        );

    const handleUpload = async () => {
        setGlobalError(null);
        const uploadable = items
            .map((it, index) => ({ it, index }))
            .filter(({ it }) => it.status === 'pending');
        if (!uploadable.length) return;

        setUploading(true);
        try {
            const presigned = await presignUploads(
                token,
                uploadable.map(({ it }) => ({
                    filename: it.file.name,
                    size: it.file.size,
                })),
            );

            // presigned[] preserves the order of the files we sent.
            for (let i = 0; i < uploadable.length; i++) {
                const { index } = uploadable[i];
                const target = presigned[i];
                updateItem(index, { status: 'uploading', progress: 0 });
                try {
                    await uploadFileToS3(
                        target.url,
                        items[index].file,
                        (percent) => updateItem(index, { progress: percent }),
                    );
                    updateItem(index, { status: 'done', progress: 100 });
                } catch {
                    updateItem(index, {
                        status: 'error',
                        error: "Échec de l'envoi",
                    });
                }
            }

            // Refresh remaining quota.
            const info = await validateUploadToken(token);
            setSession(info);
        } catch (err) {
            setGlobalError(
                getErrorMessage(
                    err,
                    "Impossible de préparer l'envoi. Réessayez ou demandez un nouveau lien.",
                ),
            );
        } finally {
            setUploading(false);
        }
    };

    const pendingCount = items.filter((i) => i.status === 'pending').length;
    const doneCount = items.filter((i) => i.status === 'done').length;

    return (
        <div className="min-h-screen w-screen bg-[#0f141a] text-[#e5e7e3] flex flex-col items-center px-4 py-10">
            <div className="w-full max-w-2xl">
                <div className="flex items-center gap-3 mb-8">
                    <img src={logo} alt="Humeos" className="h-9 w-9" />
                    <span className="text-lg font-semibold text-white">
                        Humeos — Envoi de fichiers
                    </span>
                </div>

                {phase === 'validating' && (
                    <div className="flex items-center gap-3 text-[#a8b0bb]">
                        <Loader2Icon className="h-5 w-5 animate-spin" />
                        Vérification du lien…
                    </div>
                )}

                {phase === 'invalid' && (
                    <div className="rounded-lg border border-[#1e242a] bg-[#161c22] p-6">
                        <h1 className="text-base font-semibold text-white mb-1">
                            Lien invalide
                        </h1>
                        <p className="text-sm text-[#a8b0bb]">
                            Ce lien n’existe pas ou a été désactivé. Demandez un
                            nouveau lien à votre contact Humeos.
                        </p>
                    </div>
                )}

                {phase === 'expired' && (
                    <div className="rounded-lg border border-[#1e242a] bg-[#161c22] p-6">
                        <h1 className="text-base font-semibold text-white mb-1">
                            Lien expiré
                        </h1>
                        <p className="text-sm text-[#a8b0bb]">
                            Ce lien d’envoi a expiré. Demandez un nouveau lien à
                            votre contact Humeos.
                        </p>
                    </div>
                )}

                {phase === 'ready' && session && (
                    <>
                        <div className="rounded-lg border border-[#1e242a] bg-[#161c22] p-5 mb-5">
                            <p className="text-sm text-[#d8dde2]">
                                <span className="text-[#74c69d] font-medium">
                                    {session.label}
                                </span>
                            </p>
                            <p className="text-xs text-[#a8b0bb] mt-1">
                                Déposez vos fichiers (.json, .jpg, .jp2). Valide
                                jusqu’au{' '}
                                {new Date(session.expiresAt).toLocaleString(
                                    'fr-FR',
                                )}{' '}
                                · espace restant{' '}
                                {formatBytes(session.remainingBytes)}.
                            </p>
                        </div>

                        {/* Drop zone */}
                        <div
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                addFiles(e.dataTransfer.files);
                            }}
                            onClick={() => inputRef.current?.click()}
                            className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center transition ${
                                dragging
                                    ? 'border-[#74c69d] bg-[#13231b]'
                                    : 'border-[#2a323a] bg-[#11161c] hover:border-[#3a454f]'
                            }`}
                        >
                            <UploadCloudIcon className="mx-auto h-10 w-10 text-[#6b7480]" />
                            <p className="mt-3 text-sm text-[#d8dde2]">
                                Glissez vos fichiers ici ou cliquez pour
                                parcourir
                            </p>
                            <p className="mt-1 text-xs text-[#6b7480]">
                                Formats acceptés : .json, .jpg, .jp2
                            </p>
                            <input
                                ref={inputRef}
                                type="file"
                                multiple
                                accept={ACCEPTED.join(',')}
                                className="hidden"
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                        </div>

                        {/* File list */}
                        {items.length > 0 && (
                            <div className="mt-5 space-y-2">
                                {items.map((item, index) => (
                                    <div
                                        key={`${item.file.name}-${index}`}
                                        className="flex items-center gap-3 rounded-md border border-[#1e242a] bg-[#161c22] px-3 py-2.5"
                                    >
                                        <FileIcon className="h-4 w-4 flex-shrink-0 text-[#6b7480]" />
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm text-[#d8dde2]">
                                                {item.file.name}
                                            </div>
                                            <div className="text-xs text-[#6b7480]">
                                                {formatBytes(item.file.size)}
                                                {item.status === 'uploading' &&
                                                    ` · ${item.progress}%`}
                                                {item.error && (
                                                    <span className="text-red-400">
                                                        {' '}
                                                        · {item.error}
                                                    </span>
                                                )}
                                            </div>
                                            {item.status === 'uploading' && (
                                                <div className="mt-1 h-1 w-full overflow-hidden rounded bg-[#0a0e12]">
                                                    <div
                                                        className="h-full bg-[#74c69d] transition-all"
                                                        style={{
                                                            width: `${item.progress}%`,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {item.status === 'done' && (
                                            <CheckCircle2Icon className="h-5 w-5 flex-shrink-0 text-[#74c69d]" />
                                        )}
                                        {item.status === 'error' && (
                                            <XCircleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
                                        )}
                                        {item.status === 'pending' &&
                                            !uploading && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeItem(index);
                                                    }}
                                                    className="flex-shrink-0 text-[#6b7480] hover:text-red-400"
                                                >
                                                    <Trash2Icon className="h-4 w-4" />
                                                </button>
                                            )}
                                        {item.status === 'uploading' && (
                                            <Loader2Icon className="h-4 w-4 flex-shrink-0 animate-spin text-[#74c69d]" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {globalError && (
                            <div className="mt-4 rounded-md border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
                                {globalError}
                            </div>
                        )}

                        <div className="mt-5 flex items-center justify-between">
                            <span className="text-xs text-[#6b7480]">
                                {doneCount > 0 &&
                                    `${doneCount} fichier(s) envoyé(s). `}
                                {pendingCount > 0 &&
                                    `${pendingCount} en attente.`}
                            </span>
                            <button
                                onClick={handleUpload}
                                disabled={uploading || pendingCount === 0}
                                className="inline-flex items-center gap-2 rounded-md bg-[#2f6f4e] px-5 py-2.5 text-sm font-medium text-white transition hover:bg-[#3a865f] disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {uploading ? (
                                    <Loader2Icon className="h-4 w-4 animate-spin" />
                                ) : (
                                    <UploadCloudIcon className="h-4 w-4" />
                                )}
                                Envoyer{pendingCount > 0 && ` (${pendingCount})`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
